export class FaceScannerTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FaceScannerTimeoutError';
  }
}

export type CameraInput = Pick<MediaDeviceInfo, 'deviceId' | 'groupId' | 'kind' | 'label'>;
export type CameraFacing = 'user' | 'environment' | 'unknown';

const FRONT_CAMERA_PATTERN = /front|user|selfie|facetime|true\s*depth|frontale|avant|frontal/i;
const REAR_CAMERA_PATTERN = /back|rear|environment|world|ultra\s*wide|telephoto|continuity|external/i;

let scannerModelsPromise: Promise<typeof import('@vladmandic/face-api')> | null = null;

export function loadFaceScannerModels() {
  if (!scannerModelsPromise) {
    scannerModelsPromise = (async () => {
      const faceapi = await withScannerTimeout(import('@vladmandic/face-api'), 15_000,
        'The secure scanner took too long to open. Check your connection and tap Retry scanner.');
      await withScannerTimeout(Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
        faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
        faceapi.nets.faceRecognitionNet.loadFromUri('/models'),
      ]), 30_000, 'Face recognition took too long to load. Check your connection and tap Retry scanner.');
      return faceapi;
    })().catch((error) => { scannerModelsPromise = null; throw error; });
  }
  return scannerModelsPromise;
}

export function resetFaceScannerModels() {
  scannerModelsPromise = null;
}

export function cameraFacingFromLabel(label = ''): CameraFacing {
  if (FRONT_CAMERA_PATTERN.test(label)) return 'user';
  if (REAR_CAMERA_PATTERN.test(label)) return 'environment';
  return 'unknown';
}

export function rankVideoInputs(devices: CameraInput[], preferredDeviceId = '') {
  return devices.filter((device) => device.kind === 'videoinput').map((device, index) => {
    const facing = cameraFacingFromLabel(device.label);
    const score = (device.deviceId === preferredDeviceId ? 1_000 : 0)
      + (facing === 'user' ? 200 : facing === 'environment' ? -200 : 0)
      + (/built.?in|integrated/i.test(device.label) ? 20 : 0)
      - (/continuity|external/i.test(device.label) ? 80 : 0)
      - index / 100;
    return { ...device, facing, score };
  }).sort((left, right) => right.score - left.score);
}

export function waitForVideoReady(video: HTMLVideoElement, timeoutMs = 10_000) {
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
    return Promise.resolve();
  }
  const ready = new Promise<void>((resolve, reject) => {
    const onReady = () => {
      if (video.videoWidth <= 0 || video.videoHeight <= 0) return;
      cleanup(); resolve();
    };
    const onError = () => { cleanup(); reject(new Error('The selected camera could not produce a preview.')); };
    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('error', onError);
    };
    video.addEventListener('loadedmetadata', onReady);
    video.addEventListener('canplay', onReady);
    video.addEventListener('error', onError);
  });
  return withScannerTimeout(ready, timeoutMs, 'The camera preview took too long to become ready. Tap Try camera again.');
}

export function faceFrameQuality(box: { x: number; y: number; width: number; height: number }, frameWidth: number, frameHeight: number) {
  if (!frameWidth || !frameHeight) return { ok: false, message: 'The camera preview is not ready yet.' };
  const sizeRatio = box.width / frameWidth;
  if (sizeRatio < 0.2) return { ok: false, message: 'Move a little closer so your face fills the guide.' };
  if (sizeRatio > 0.72) return { ok: false, message: 'Move a little farther back so your full face is visible.' };
  const faceCenterX = box.x + box.width / 2;
  const faceCenterY = box.y + box.height / 2;
  const horizontalOffset = Math.abs(faceCenterX / frameWidth - 0.5);
  const verticalOffset = Math.abs(faceCenterY / frameHeight - 0.46);
  if (horizontalOffset > 0.2 || verticalOffset > 0.23) {
    return { ok: false, message: 'Center your face inside the oval guide.' };
  }
  return { ok: true, message: '' };
}

export function withScannerTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new FaceScannerTimeoutError(message)), timeoutMs);
    promise.then(
      (value) => { window.clearTimeout(timer); resolve(value); },
      (error) => { window.clearTimeout(timer); reject(error); },
    );
  });
}

export function cameraErrorMessage(error: unknown) {
  const named = error as { name?: string; message?: string };
  if (named.name === 'NotAllowedError' || named.name === 'SecurityError') {
    return 'Camera permission is blocked. Open device Settings > Nosivo (or Apps > Nosivo) > Camera, choose Allow, then return and tap Try camera again.';
  }
  if (named.name === 'NotFoundError' || named.name === 'DevicesNotFoundError') {
    return 'No front camera was found on this device.';
  }
  if (named.name === 'NotReadableError' || named.name === 'TrackStartError') {
    return 'The camera is being used by another app. Close other camera or video apps, then try again.';
  }
  if (named.name === 'OverconstrainedError' || named.name === 'ConstraintNotSatisfiedError') {
    return 'The front camera could not start with the required settings. Tap Try camera again.';
  }
  return named.message || 'The camera could not start. Check the Nosivo camera permission and try again.';
}

export function locationErrorMessage(error: unknown) {
  const positioned = error as { code?: number; message?: string };
  if (positioned.code === 1) {
    return 'Location permission is blocked. Open device Settings > Nosivo (or Apps > Nosivo) > Location, choose While Using the App and Precise Location, then try again.';
  }
  if (positioned.code === 2) return 'Your location is unavailable. Turn on Location and try again.';
  if (positioned.code === 3) return 'Location took too long. Move near a window or enable precise location, then try again.';
  return positioned.message || 'Trusted-site location could not be confirmed. Check the Nosivo location permission and try again.';
}

export function faceScannerFailureCode(error: unknown, stage = 'scan') {
  const value = error as { name?: string; details?: { code?: unknown }; message?: string };
  if (value.details?.code) return String(value.details.code).slice(0, 80);
  if (value.name === 'NotAllowedError' || value.name === 'SecurityError') return 'CAMERA_PERMISSION_BLOCKED';
  if (value.name === 'NotFoundError' || value.name === 'DevicesNotFoundError') return 'FRONT_CAMERA_NOT_FOUND';
  if (value.name === 'NotReadableError' || value.name === 'TrackStartError') return 'CAMERA_IN_USE';
  if (value.name === 'GeolocationPositionError') return 'LOCATION_UNAVAILABLE';
  if (value instanceof FaceScannerTimeoutError) return `${stage.toUpperCase()}_TIMEOUT`;
  if (/location|trusted site|office/i.test(value.message || '')) return 'SITE_OR_LOCATION_FAILED';
  if (/face|lighting|camera/i.test(value.message || '')) return 'FACE_CAPTURE_FAILED';
  return `${stage.toUpperCase()}_FAILED`;
}
